import type { Permission } from './permissions';

/**
 * Module registry — the single source for the main menu, routes, page titles, breadcrumbs, page
 * permissions and module status (ADR 0010). The web app maps `key` → page component in
 * `apps/web/src/app/routes.tsx`; nothing else defines navigation.
 *
 * `frCodes` ties a module to its functional requirements in `doc/requirements.md`
 * (`FR-<CODE>-NNN`). Each FR code belongs to exactly one module. A module may only be
 * `ready` when every non-cancelled requirement of its codes has a test (check C13).
 */

export type ModuleStatus = 'planned' | 'in-progress' | 'ready';

/**
 * - `primary`   — always-visible main menu bar
 * - `overflow`  — the "more" (⋯) menu
 * - `action`    — end of the overflow menu, after a separator (e.g. data export)
 * - `settings`  — workspace settings menu
 * - `none`      — not in a menu (detail pages, core capabilities)
 */
export type NavPlacement = 'primary' | 'overflow' | 'action' | 'settings' | 'none';

export interface ModuleDef {
  readonly key: string;
  /** Route of the module's page; `null` for capabilities without a page of their own. */
  readonly path: string | null;
  readonly labelKey: string;
  readonly icon: string;
  /** Permission required to see the menu entry and open the page. */
  readonly permission: Permission | null;
  readonly placement: NavPlacement;
  readonly status: ModuleStatus;
  readonly frCodes: readonly string[];
}

export const MODULES = [
  // Primary bar (FR-NAV-001)
  {
    key: 'dashboard',
    path: '/',
    labelKey: 'nav.dashboard',
    icon: 'home',
    permission: 'dashboard.view',
    placement: 'primary',
    status: 'ready',
    frCodes: ['DASH'],
  },
  {
    key: 'turnover',
    path: '/turnover',
    labelKey: 'nav.turnover',
    icon: 'trending-up',
    permission: 'finance.view',
    placement: 'primary',
    status: 'ready',
    frCodes: ['TURN'],
  },
  {
    key: 'customers',
    path: '/customers',
    labelKey: 'nav.customers',
    icon: 'building',
    permission: 'customers.view',
    placement: 'primary',
    status: 'ready',
    frCodes: ['CUST'],
  },
  {
    key: 'support',
    path: '/support',
    labelKey: 'nav.support',
    icon: 'life-buoy',
    permission: 'support.view',
    placement: 'primary',
    status: 'ready',
    frCodes: ['SUP'],
  },
  {
    key: 'reconciliation',
    path: '/reconciliation',
    labelKey: 'nav.reconciliation',
    icon: 'check-check',
    permission: 'reconciliation.manage',
    placement: 'primary',
    status: 'ready',
    frCodes: ['JIRA'],
  },
  // Overflow menu (FR-NAV-002)
  {
    key: 'overview',
    path: '/overview',
    labelKey: 'nav.overview',
    icon: 'layout-grid',
    permission: 'overview.view',
    placement: 'overflow',
    status: 'ready',
    frCodes: ['OVR'],
  },
  {
    key: 'roadmap',
    path: '/roadmap',
    labelKey: 'nav.roadmap',
    icon: 'map',
    permission: 'finance.view',
    placement: 'overflow',
    status: 'ready',
    frCodes: [],
  },
  {
    key: 'tasks',
    path: '/tasks',
    labelKey: 'nav.tasks',
    icon: 'list-checks',
    permission: 'tasks.view',
    placement: 'overflow',
    status: 'ready',
    frCodes: ['TASK'],
  },
  {
    key: 'time-overview',
    path: '/time-overview',
    labelKey: 'nav.timeOverview',
    icon: 'clock',
    permission: 'overview.view',
    placement: 'overflow',
    status: 'ready',
    frCodes: [],
  },
  {
    key: 'business-cycle',
    path: '/business-cycle',
    labelKey: 'nav.businessCycle',
    icon: 'refresh-cw',
    permission: 'bizcycle.view',
    placement: 'overflow',
    status: 'ready',
    frCodes: ['BIZ'],
  },
  {
    key: 'technologies',
    path: '/technologies',
    labelKey: 'nav.technologies',
    icon: 'cpu',
    permission: 'technologies.view',
    placement: 'overflow',
    status: 'ready',
    frCodes: ['TECH'],
  },
  {
    key: 'resources',
    path: '/resources',
    labelKey: 'nav.resources',
    icon: 'users',
    permission: 'resources.view',
    placement: 'overflow',
    status: 'ready',
    frCodes: ['RES'],
  },
  // Overflow actions (FR-NAV-003)
  {
    key: 'export',
    path: '/export',
    labelKey: 'nav.export',
    icon: 'download',
    permission: 'export.run',
    placement: 'action',
    status: 'planned',
    frCodes: ['EXP'],
  },
  // Workspace settings
  {
    key: 'workspace',
    path: '/settings/members',
    labelKey: 'nav.members',
    icon: 'user-cog',
    permission: 'members.view',
    placement: 'settings',
    status: 'ready',
    frCodes: ['WS'],
  },
  {
    key: 'sessions',
    path: '/settings/sessions',
    labelKey: 'nav.sessions',
    icon: 'monitor-smartphone',
    permission: null,
    placement: 'settings',
    status: 'ready',
    frCodes: [],
  },
  {
    key: 'api-tokens',
    path: '/settings/api-tokens',
    labelKey: 'nav.apiTokens',
    icon: 'key-round',
    permission: 'apitokens.manage',
    placement: 'settings',
    status: 'ready',
    frCodes: ['MCP'],
  },
  {
    key: 'jira-settings',
    path: '/settings/jira',
    labelKey: 'nav.jiraSettings',
    icon: 'plug',
    permission: 'integrations.manage',
    placement: 'settings',
    status: 'ready',
    frCodes: [],
  },
  // Pages without a menu entry
  {
    key: 'projects',
    path: '/projects',
    labelKey: 'nav.projects',
    icon: 'folder',
    permission: 'projects.view',
    placement: 'none',
    status: 'ready',
    frCodes: ['PRJ'],
  },
  {
    key: 'timesheets',
    path: null,
    labelKey: 'nav.timesheets',
    icon: 'table',
    permission: 'timesheets.view',
    placement: 'none',
    status: 'ready',
    frCodes: ['TS'],
  },
  {
    key: 'ai',
    path: null,
    labelKey: 'nav.ai',
    icon: 'sparkles',
    permission: 'ai.run',
    placement: 'none',
    status: 'planned',
    frCodes: ['AI'],
  },
  // Capabilities without a page of their own
  {
    key: 'auth',
    path: null,
    labelKey: 'nav.login',
    icon: 'log-in',
    permission: null,
    placement: 'none',
    status: 'ready',
    frCodes: ['AUTH'],
  },
  {
    key: 'shell',
    path: null,
    labelKey: 'nav.dashboard',
    icon: 'panel-top',
    permission: null,
    placement: 'none',
    status: 'ready',
    frCodes: ['NAV'],
  },
] as const satisfies readonly ModuleDef[];

export type ModuleKey = (typeof MODULES)[number]['key'];

export function moduleByKey(key: ModuleKey): ModuleDef {
  const found = MODULES.find((m) => m.key === key);
  if (!found) throw new Error(`Unknown module: ${key}`);
  return found;
}

/** Modules that own a page (and therefore a route). */
export function pageModules(): readonly (ModuleDef & { path: string })[] {
  return MODULES.filter((m): m is (typeof MODULES)[number] & { path: string } => m.path !== null);
}

export function modulesByPlacement(placement: NavPlacement): readonly ModuleDef[] {
  return MODULES.filter((m) => m.placement === placement);
}

/** Visible menu entries for a set of granted permissions, in registry order. */
export function visibleModules(
  placement: NavPlacement,
  granted: readonly Permission[],
): readonly ModuleDef[] {
  return modulesByPlacement(placement).filter((m) => m.permission === null || granted.includes(m.permission));
}

/** Module owning an FR code (`FR-CUST-001` → customers). */
export function moduleForFrCode(code: string): ModuleDef | undefined {
  return MODULES.find((m) => (m.frCodes as readonly string[]).includes(code));
}
